import React from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import type { FeedbackEntry } from '../types';
import { FeedbackHistoryList } from './FeedbackHistoryList';
import { FeedbackListPagination } from './FeedbackListPagination';

type RecentFeedbackTabProps = {
  entries: FeedbackEntry[];
  page: number;
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onPageChange: (page: number) => void;
};

export const RecentFeedbackTab: React.FC<RecentFeedbackTabProps> = ({
  entries,
  page,
  totalCount,
  isLoading,
  isFetching,
  isError,
  onPageChange,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">Failed to load recent feedback.</Alert>;
  }

  if (entries.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
        <HistoryIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
        <Typography variant="body2">No recent feedback found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', opacity: isFetching ? 0.6 : 1 }}>
      {isFetching && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            py: 2,
            zIndex: 1,
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}
      <FeedbackHistoryList entries={entries} />
      <FeedbackListPagination page={page} totalCount={totalCount} onPageChange={onPageChange} />
    </Box>
  );
};
